import type { BaseFormInputProps } from "@/Type/BaseFormInputProps";
import styled from "styled-components";
import { formatToUKDate } from "@/Util/formatToUKDate";
import {Audit} from "@/Stub/V1";
import {formatFileSize, snakeToNormalCase, toTitle} from "@/Helpers/general";
import {mapFieldsData, mapFieldsTable} from "@/Helpers/forms";
import {getUserAvatar} from "@/Helpers/user";

const StyledWrap = styled.div`
    .table {
        width: 100%;
        margin: 0.5rem 0 1rem 0;

        thead {
            th {
                text-align: left;
                padding: 1rem 0.5rem;
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid var(--gray-200);

                &:hover {
                    background-color: var(--gray-100);
                }

                &:last-child {
                    border: 0;
                }
            }

            td {
                padding: 1rem 0.5rem;
            }
        }
    }
`;

export type AuditPreviewProps = BaseFormInputProps & {
    audit?: Audit
};

const AuditPreview = ({ audit }: AuditPreviewProps) => {
    if (!audit) return undefined;
    return (
        <StyledWrap>
            <div className="user-info mb-3 flex">
                {getUserAvatar(audit.user || {})}

                <div>
                    <div className="user-name">{audit.user ? audit.user?.name : 'System'}</div>
                    <div className="audit-date">{formatToUKDate(audit.created_at, true)}</div>
                </div>
            </div>

            <div className="audit-description">{audit.detail}</div>

            {audit.type === 'create_file' && (
                <table className="simple-table">
                    <tbody>
                    {audit?.more_detail?.file_info.map((file) => {
                        return (
                            <tr key={file.id}>
                                <td className="file-name">{file.name}</td>
                                <td>{formatFileSize(file.size)}</td>
                                <td>{file.mime}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            )}

            {audit.type === 'excel_import_failures' && (
                <>
                    <div className={'my-2'}>File name: {audit.more_detail.file_name}</div>
                    <table className="simple-table">
                        <thead>
                            <tr>
                                <th align="left">#</th>
                                <th align="left">Row</th>
                                <th align="left">Column</th>
                                <th align="left">Error</th>
                                <th align="left">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                        {audit?.more_detail?.errors.map((error, index) => {
                            return (
                                <tr key={index}>
                                    <td align="left">{index + 1}</td>
                                    <td align="left">{error.row}</td>
                                    <td align="left">{error.attribute}</td>
                                    <td align="left">{error.errors?.pop()}</td>
                                    <td align="left">{error.used_value}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </>
            )}

            {audit.type === 'update_case' && (
                <table className="simple-table">
                    <tbody>
                    {mapFieldsData(audit).map((field) => {
                        return (
                            <tr key={field.name}>
                                <td className="file-name">{field.name}</td>
                                <td>{field.value}</td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            )}

            {audit.type === 'rule_applied' && (
                <table className="simple-table">
                    <tbody>
                    {Object.keys(audit.more_detail.data).map((relation, index) => (
                        <>
                            <tr key={`title-${relation}`}>
                                <th align="left" colSpan={3}>
                                    {toTitle(relation)}
                                </th>
                            </tr>
                            {mapFieldsTable(audit.more_detail.data[relation]).map((field, fieldIndex) => (
                                <tr key={`${index}-${relation}-${field.name}-${fieldIndex}`}>
                                    <td>{field.name}</td>
                                    <td>{field.type}</td>
                                    <td>{field.value}</td>
                                </tr>
                            ))}
                           <br/>
                      </>
                    ))}
                    </tbody>
                </table>
            )}

            {audit.type === 'offer_amended' && (
                <table className="simple-table">
                    <tbody>
                    <>
                        <tr>
                            <th align="left" colSpan={2}>
                                Rationale
                            </th>
                        </tr>
                        <tr>
                            <td align="left">
                                {audit.more_detail.data.rationale}
                            </td>
                        </tr>
                        <br/>
                        <tr>
                            <th align="left" colSpan={2}>
                                Before
                            </th>
                        </tr>
                        {Object.keys(audit.more_detail.data.before).sort().map((item) =>
                            <tr key={`before-${item}`}>
                                <td>
                                    {snakeToNormalCase(item)}
                                </td>
                                <td>
                                    {audit.more_detail.data.before[item]}
                                </td>
                            </tr>)
                        }
                        <br/>
                        <tr>
                            <th align="left" colSpan={2}>
                                After
                            </th>
                        </tr>
                        {Object.keys(audit.more_detail.data.after).sort().map((item) =>
                            <tr key={`after-${item}`}>
                                <td>
                                    {snakeToNormalCase(item)}
                                </td>
                                <td>
                                    {audit.more_detail.data.after[item]}
                                </td>
                            </tr>)
                        }
                    </>
                    </tbody>
                </table>
            )}

            {audit.type === 'create_note' && (
                <div className="p-3">{audit.more_detail?.description}</div>
            )}
        </StyledWrap>
    );
};

export default AuditPreview;
